import React, { useState } from 'react';
import { HiOutlineUpload, HiOutlineDocumentText } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import './BulkUploadModal.css';

// Bulk-imports deliveries from an ERP invoice export (.xlsx/.xls/.csv). The
// file has no delivery-boy column, so one can optionally be picked here for
// the whole batch; left blank, the batch is created unassigned. Backend
// skips bad rows and reports them rather than failing the
// whole upload — see deliveryController.js's bulkUploadDeliveries.
// Kept in sync with backend_reception/src/config/excelUploadConfig.js's
// allowedExtensions — checking here too means a wrong file gets caught the
// moment it's picked, not after a round trip to the server.
const ALLOWED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

// `scope` is the admin's currently-selected { company, branch } — the branch
// the whole batch is filed into, shown back to them as `scopeLabel` before they
// upload. null for a normal user, whose own branch the server stamps.
const BulkUploadModal = ({ onClose, deliveryBoys, onUploaded, scope, scopeLabel }) => {
    const [file, setFile] = useState(null);
    const [deliveryBoy, setDeliveryBoy] = useState('');
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleFileChange = (e) => {
        const selected = e.target.files[0] || null;
        setResult(null);
        setError('');

        if (!selected) {
            setFile(null);
            return;
        }

        const ext = `.${selected.name.split('.').pop().toLowerCase()}`;
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            setFile(null);
            e.target.value = ''; // let the user pick the same filename again after fixing it
            setError(`"${selected.name}" is not a supported file type. Please upload an Excel (.xlsx, .xls) or CSV (.csv) file.`);
            return;
        }

        setFile(selected);
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Please choose a file to upload.');
            return;
        }

        // An admin uploading must have picked a branch first — every row in the
        // batch is filed into it, so guessing their own would silently import
        // someone else's deliveries into Head Office.
        if (scope && (!scope.company || !scope.branch)) {
            setError('Please choose a company and branch at the top of the page first. As an administrator you can upload deliveries for any branch, so we need to know which one this file belongs to.');
            return;
        }

        setError('');
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (deliveryBoy) formData.append('deliveryBoy', deliveryBoy);
            // Ignored by the server for a normal user, who gets their own branch.
            if (scope?.company && scope?.branch) {
                formData.append('company', scope.company);
                formData.append('branch', scope.branch);
            }

            const response = await axiosInstance.post('/api/deliveries/bulk-upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120000
            });

            if (response.data.success) {
                const data = response.data.data;
                setResult(data);
                if (data.created > 0 && onUploaded) {
                    onUploaded();
                }
                // Every other action in this module confirms itself with an
                // alert (see DeliveryManagement's delete, DeliveryForm's save).
                // Without one here the results panel below is the only feedback,
                // and it is missed entirely by anyone who closes the modal by
                // clicking outside it — which reads as "the upload did nothing".
                if (data.created > 0) {
                    alert(
                        `${data.created} ${data.created === 1 ? 'delivery' : 'deliveries'} uploaded successfully` +
                        (scopeLabel ? ` to ${scopeLabel}` : '') +
                        (data.skipped > 0 ? `.\n\n${data.skipped} row(s) were skipped — see the list for the reasons.` : '.')
                    );
                } else {
                    alert(
                        `No deliveries were uploaded — all ${data.skipped} row(s) were skipped.\n\nSee the list for the reason for each row.`
                    );
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload file. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="dlv-bulk-upload-overlay" onClick={onClose}>
            <div className="dlv-bulk-upload-modal" onClick={(e) => e.stopPropagation()}>
                <div className="dlv-bulk-upload-header">
                    <h2>Bulk Upload Deliveries</h2>
                    <button className="dlv-bulk-upload-close" onClick={onClose}>×</button>
                </div>

                <div className="dlv-bulk-upload-body">
                    {!result ? (
                        <>
                            {scope && (
                                <div className={`dlv-bulk-upload-scope${scopeLabel ? '' : ' dlv-bulk-upload-scope-missing'}`}>
                                    {scopeLabel ? (
                                        <>Uploading to <strong>{scopeLabel}</strong>. Change it with the "Viewing data for" selector behind this window.</>
                                    ) : (
                                        <>Choose a company and branch with the "Viewing data for" selector behind this window before uploading.</>
                                    )}
                                </div>
                            )}

                            <div className="dlv-bulk-upload-section">
                                <label>Delivery Boy</label>
                                <p className="dlv-bulk-upload-hint">
                                    The uploaded file has no delivery-boy column. Pick someone to assign every delivery in this batch to them, or leave it unassigned to assign rows individually later from the admin panel.
                                </p>
                                <select
                                    value={deliveryBoy}
                                    onChange={(e) => setDeliveryBoy(e.target.value)}
                                    className="dlv-bulk-upload-select"
                                >
                                    <option value="">Unassigned</option>
                                    {deliveryBoys.map((boy) => (
                                        <option key={boy._id} value={boy._id}>{boy.fullName}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="dlv-bulk-upload-section">
                                <label>File <span className="dlv-bulk-upload-required">*</span></label>
                                <p className="dlv-bulk-upload-hint">
                                    Accepts .xlsx, .xls or .csv. Expected columns: Invoice Number, Customer Name, Customer Code (optional), Sales Engineer Code (optional), Amount.
                                </p>
                                <label className="dlv-bulk-upload-dropzone">
                                    <HiOutlineDocumentText className="dlv-bulk-upload-dropzone-icon" />
                                    <span>{file ? file.name : 'Click to choose a file'}</span>
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        onChange={handleFileChange}
                                        hidden
                                    />
                                </label>
                            </div>

                            {error && <div className="dlv-bulk-upload-error">{error}</div>}
                        </>
                    ) : (
                        <div className="dlv-bulk-upload-results">
                            <div className="dlv-bulk-upload-summary">
                                <div className="dlv-bulk-upload-stat dlv-bulk-upload-stat-created">
                                    <span className="dlv-bulk-upload-stat-value">{result.created}</span>
                                    <span className="dlv-bulk-upload-stat-label">Created</span>
                                </div>
                                <div className="dlv-bulk-upload-stat dlv-bulk-upload-stat-skipped">
                                    <span className="dlv-bulk-upload-stat-value">{result.skipped}</span>
                                    <span className="dlv-bulk-upload-stat-label">Skipped</span>
                                </div>
                            </div>

                            {result.errors && result.errors.length > 0 && (
                                <div className="dlv-bulk-upload-errors-list">
                                    <h4>Skipped rows</h4>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Row</th>
                                                <th>Invoice</th>
                                                <th>Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.errors.map((e, idx) => (
                                                <tr key={idx}>
                                                    <td>{e.row}</td>
                                                    <td>{e.invoiceNo || '—'}</td>
                                                    <td>{e.reason}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="dlv-bulk-upload-footer">
                    {!result ? (
                        <>
                            <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
                                {uploading ? (
                                    <>
                                        <span className="spinner-small"></span>
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <HiOutlineUpload /> Upload
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        <button className="btn btn-primary" onClick={onClose}>
                            Done
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkUploadModal;
